import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Phone,
  Copy,
  HelpCircle,
  ChevronDown,
  Play,
  Pause,
  Mic,
  Languages,
  Volume2,
  Settings2
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { chatbotService, ChatSettings } from "@/services/chatbotService";
import { teamService } from "@/services/teamService";
import { useProjectSelection } from "@/contexts/ProjectSelectionContext";

interface VoiceOption {
  id: string;
  name: string;
  description: string;
  previewUrl: string; // Placeholder for now
}

interface BackgroundSoundOption {
  id: string;
  name: string;
  description: string;
}

export default function VoiceSettings() {
  const { toast } = useToast();
  const { effectiveProjectId } = useProjectSelection();
  const isGodModeEnabled = typeof window !== "undefined" && window.location.href.toLowerCase().includes("godmode");

  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Phone Number State
  const [sophiePhoneNumber, setSophiePhoneNumber] = useState("");
  const [defaultTransferNumber, setDefaultTransferNumber] = useState("");
  const [isForwardingHelpOpen, setIsForwardingHelpOpen] = useState(false);
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);

  // Language State
  const [language, setLanguage] = useState("en");

  // Voice State
  const [selectedVoice, setSelectedVoice] = useState("");
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);

  const voicesByLanguage: Record<string, VoiceOption[]> = {
    en: [
      { id: "187d1cc5-a771-4ccd-9110-9df8c4e39499", name: "Sophie", description: "Firm adult female great for providing clear instructions", previewUrl: "" },
      { id: "ce74c4da-4aee-435d-bc6d-81d1a9367e12", name: "Marc", description: "Friendly adult male for casual conversations", previewUrl: "" },
      { id: "22f1a356-56c2-4428-bc91-2ab2e6d0c215", name: "Iza", description: "Formal adult female for professional conversations", previewUrl: "" },
      { id: "996a8b96-4804-46f0-8e05-3fd4ef1a87cd", name: "Darla", description: "(Canadian English) Firm and confident female voice with a calm, supportive tone - ideal for customer support roles", previewUrl: "" },
      { id: "9626c31c-bec5-4cca-baa8-f8ba9e84c8bc", name: "Jacqueline", description: "(American English) Confident, young adult female for empathic customer support", previewUrl: "" },
      { id: "1242fb95-7ddd-44ac-8a05-9e8a22a6137d", name: "Cindy", description: "(American English) Smooth, welcoming adult female for frontline customer interactions", previewUrl: "" }


    ],
    fr: [
      { id: "187d1cc5-a771-4ccd-9110-9df8c4e39499", name: "Sophie", description: "Chaleureuse et professionnelle. Bonne pour les conversations professionnelles.", previewUrl: "" },
      { id: "ce74c4da-4aee-435d-bc6d-81d1a9367e12", name: "Marc", description: "Amical et dynamique. Bonne pour les conversations informelles.", previewUrl: "" },
    ],
    "fr-en": [
      { id: "187d1cc5-a771-4ccd-9110-9df8c4e39499", name: "Sophie", description: "Firm adult female great for providing clear instructions", previewUrl: "" },
      { id: "ce74c4da-4aee-435d-bc6d-81d1a9367e12", name: "Marc", description: "Amical et dynamique. Bonne pour les conversations informelles.", previewUrl: "" }
    ],
    "auto": [
      { id: "187d1cc5-a771-4ccd-9110-9df8c4e39499", name: "Sophie", description: "Firm adult female great for providing clear instructions", previewUrl: "" },
      { id: "ce74c4da-4aee-435d-bc6d-81d1a9367e12", name: "Marc", description: "Amical et dynamique. Bonne pour les conversations informelles.", previewUrl: "" }
    ]
  };

  // Get current voices list based on language (default to en if not found)
  const voices = voicesByLanguage[language] || voicesByLanguage['en'];

  useEffect(() => {
    // If current selected voice is not in the new language list, select the first one
    if (voices.length > 0) {
      const found = voices.find(v => v.id === selectedVoice);
      if (!found) {
        setSelectedVoice(voices[0].id);
      }
    }
  }, [language, voices, selectedVoice]);

  // Background Sound State
  const [backgroundSound, setBackgroundSound] = useState("none");
  const [playingBackground, setPlayingBackground] = useState<string | null>(null);

  const backgroundSounds: BackgroundSoundOption[] = [
    { id: "none", name: "None", description: "No background noise" },
    { id: "bar_background", name: "Bar", description: "Subtle ambient noise of a busy bar or cafe" },
    { id: "mechanic_background", name: "Mechanic", description: "Workshop sounds, light tools and activity" },
    { id: "callcenter_background", name: "Call Center", description: "Professional office environment with distant chatter" },
  ];

  // Advanced State
  const [greetingMessage, setGreetingMessage] = useState("");
  const [instruction, setInstruction] = useState("");
  const [voiceRecognition, setVoiceRecognition] = useState("DeepGram");
  const [restaurantCode, setRestaurantCode] = useState("");
  const [googleCalendarId, setGoogleCalendarId] = useState("");
  const [askLastname, setAskLastname] = useState(true);
  const [maxGroupSize, setMaxGroupSize] = useState(-1);
  const [isMaxGroupSizeEnabled, setIsMaxGroupSizeEnabled] = useState(false);
  const [transferTakeout, setTransferTakeout] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';

  useEffect(() => {
    loadSettings();
  }, [effectiveProjectId]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await chatbotService.getSettings(effectiveProjectId ?? undefined);
      const team = await teamService.getTeam();
      setGoogleCalendarId(team.googleCalendarId ?? "");

      if (data) {
        setSettings(data);

        // Map fields
        if (data.phoneNumber) setSophiePhoneNumber(data.phoneNumber);
        setDefaultTransferNumber(data.defaultTransferNumber ?? "");
        if (data.language) setLanguage(data.language ?? "auto");
        if (data.voiceId) setSelectedVoice(data.voiceId);
        if (data.backgroundSound) setBackgroundSound(data.backgroundSound);
        if (data.customGreeting) setGreetingMessage(data.customGreeting);
        if (data.customInstructions) setInstruction(data.customInstructions);
        if (data.sttProvider) setVoiceRecognition(data.sttProvider);
        if (data.restaurantCode) setRestaurantCode(data.restaurantCode);
        setAskLastname(data.askLastname ?? true);
        const loadedMaxGroupSize = data.maxGroupSize ?? -1;
        setMaxGroupSize(loadedMaxGroupSize);
        setIsMaxGroupSizeEnabled(loadedMaxGroupSize > 0);
        setTransferTakeout(data.transferTakeout ?? false);
      } else {
        // Create default if not exists
        try {
          const newData = await chatbotService.createSettings(effectiveProjectId ?? undefined);
          setSettings(newData);
          // Defaults are set in backend create logic
          if (newData.phoneNumber) setSophiePhoneNumber(newData.phoneNumber);
          setDefaultTransferNumber(newData.defaultTransferNumber ?? "");
          if (newData.language) setLanguage(newData.language ?? "auto");
          if (newData.voiceId) setSelectedVoice(newData.voiceId);
          if (newData.backgroundSound) setBackgroundSound(newData.backgroundSound);
          if (newData.customGreeting) setGreetingMessage(newData.customGreeting);
          if (newData.customInstructions) setInstruction(newData.customInstructions);
          if (newData.sttProvider) setVoiceRecognition(newData.sttProvider);
          if (newData.restaurantCode) setRestaurantCode(newData.restaurantCode);
          setAskLastname(newData.askLastname ?? true);
          const createdMaxGroupSize = newData.maxGroupSize ?? -1;
          setMaxGroupSize(createdMaxGroupSize);
          setIsMaxGroupSizeEnabled(createdMaxGroupSize > 0);
          setTransferTakeout(newData.transferTakeout ?? false);
        } catch (createError) {
          console.error("Failed to create default settings", createError);
        }
      }
    } catch (error) {
      console.error("Failed to load voice settings", error);
      toast({
        title: "Error",
        description: "Failed to load voice settings",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingVoice(null);
    setPlayingBackground(null);
  };

  const handlePlayVoice = (id: string) => {
    stopAudio();
    if (playingVoice === id) {
      return; // Already stopped above
    }

    setPlayingVoice(id);
    const voice = voices.find(v => v.id === id);
    const text = `Hi, I'm ${voice?.name}. I can help answer your calls.`;
    const url = `${API_BASE_URL}/Chat/voice-preview?voiceId=${id}&text=${encodeURIComponent(text)}`;

    const audio = new Audio(url);
    audioRef.current = audio;

    audio.play().catch(e => {
      console.error("Failed to play audio", e);
      stopAudio();
      toast({ title: "Playback Failed", description: "Could not play voice preview.", variant: "destructive" });
    });

    audio.onended = () => {
      setPlayingVoice(null);
      audioRef.current = null;
    };
  };

  const handlePlayBackground = (id: string) => {
    stopAudio();
    if (playingBackground === id) {
      return;
    }

    setPlayingBackground(id);
    const url = `${API_BASE_URL}/Chat/background-preview?name=${id}`;

    const audio = new Audio(url);
    audioRef.current = audio;

    audio.play().catch(e => {
      console.error("Failed to play audio", e);
      stopAudio();
      toast({ title: "Playback Failed", description: "Could not play background sound.", variant: "destructive" });
    });

    audio.onended = () => {
      setPlayingBackground(null);
      audioRef.current = null;
    };
  };

  const saveSettings = async () => {
    try {
      const targetProjectId = effectiveProjectId ?? null;

      let targetSettings = await chatbotService.getSettings(targetProjectId ?? undefined);
      if (!targetSettings) {
        targetSettings = await chatbotService.createSettings(targetProjectId ?? undefined);
      }
      setSettings(targetSettings);

      const updatedSettings: ChatSettings = {
        ...targetSettings,
        projectId: targetProjectId,
        teamId: targetSettings.teamId,
        language,
        voiceId: selectedVoice,
        backgroundSound,
        customGreeting: greetingMessage,
        customInstructions: instruction,
        sttProvider: voiceRecognition,
        defaultTransferNumber,
        restaurantCode,
        askLastname,
        maxGroupSize: isMaxGroupSizeEnabled ? Math.max(1, maxGroupSize) : -1,
        transferTakeout,
      };

      await chatbotService.updateSettings(updatedSettings);
      await teamService.updateTeam({ googleCalendarId: googleCalendarId.trim() });
      setSettings(updatedSettings);

      toast({
        title: "Settings Saved",
        description: "Your voice settings have been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to save settings", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Loading settings...</div>;
  }

  return (
    <div className="space-y-6 pb-20 lg:pb-6">

      {/* Sophie Phone Number Card - Moved from YourBusiness */}
      <Card className="p-6 card-hover border-primary/20">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Phone className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Sophie's Phone Number</h2>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sophiePhone">Sophie's Phone Number</Label>
            <div className="flex gap-2">
              <Input
                id="sophiePhone"
                type="tel"
                value={sophiePhoneNumber || "Not assigned"}
                readOnly
                className="bg-muted/50 cursor-default"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  if (sophiePhoneNumber) {
                    navigator.clipboard.writeText(sophiePhoneNumber);
                    toast({
                      title: "Copied!",
                      description: "Sophie's phone number copied to clipboard",
                    });
                  }
                }}
                disabled={!sophiePhoneNumber}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-muted/50 border border-border">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Phone className="w-4 h-4 text-primary" />
              About This Number
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This is Sophie's dedicated phone number. Set up call forwarding from your business phone to this number so Sophie can answer calls when you're unavailable. Sophie will answer professionally, handle inquiries, book appointments, and send you a summary via SMS.
            </p>
          </div>

          <Collapsible open={isForwardingHelpOpen} onOpenChange={setIsForwardingHelpOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-4 h-auto">
                <span className="flex items-center gap-2 font-medium">
                  <HelpCircle className="w-4 h-4 text-primary" />
                  How to Set Up Call Forwarding
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isForwardingHelpOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                <div>
                  <h4 className="font-medium text-sm mb-2">Most Phone Carriers:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                    <li><strong>Forward all calls:</strong> Dial <code className="bg-background px-1.5 py-0.5 rounded text-foreground">*72</code> followed by {sophiePhoneNumber}, then press call</li>
                    <li><strong>Forward when busy/no answer:</strong> Dial <code className="bg-background px-1.5 py-0.5 rounded text-foreground">*71</code> followed by {sophiePhoneNumber}, then press call</li>
                    <li><strong>Disable forwarding:</strong> Dial <code className="bg-background px-1.5 py-0.5 rounded text-foreground">*73</code> and press call</li>
                  </ul>
                </div>
                <div className="flex items-start gap-2 text-sm text-muted-foreground bg-background/50 p-3 rounded">
                  <span className="text-base">⚠️</span>
                  <p>Instructions may vary by carrier. Contact your phone provider for specific steps.</p>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          <div className="space-y-2">
            <Label htmlFor="defaultTransferNumber">Default Transfer Number</Label>
            <Input
              id="defaultTransferNumber"
              type="tel"
              placeholder="e.g. +14384381234"
              value={defaultTransferNumber}
              onChange={(e) => setDefaultTransferNumber(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This is the default number Sophie uses to redirect the caller to a real person.
            </p>
          </div>
        </div>
      </Card>

      {/* Language Selection */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Languages className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Language Spoken</h2>
        </div>

        <RadioGroup value={language} onValueChange={setLanguage} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <RadioGroupItem value="en" id="lang-english" className="peer sr-only" />
            <Label
              htmlFor="lang-english"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <span className="text-lg font-semibold mb-1">English</span>
            </Label>
          </div>
          <div>
            <RadioGroupItem value="fr" id="lang-french" className="peer sr-only" />
            <Label
              htmlFor="lang-french"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <span className="text-lg font-semibold mb-1">French</span>
            </Label>
          </div>
          <div>
            <RadioGroupItem value="auto" id="lang-auto" className="peer sr-only" />
            <Label
              htmlFor="lang-auto"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <span className="text-lg font-semibold mb-1">Auto</span>
            </Label>
          </div>
        </RadioGroup>
      </Card>

      {/* Voice Selection */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Mic className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Assistant Voice</h2>
        </div>

        <RadioGroup value={selectedVoice} onValueChange={setSelectedVoice} className="space-y-3">
          {voices.map((voice) => (
            <div key={voice.id} className="relative">
              <RadioGroupItem value={voice.id} id={`voice-${voice.id}`} className="peer sr-only" />
              <Label
                htmlFor={`voice-${voice.id}`}
                className="flex items-center gap-4 rounded-lg border p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
              >
                <div
                  className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors z-10"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePlayVoice(voice.id);
                  }}
                >
                  {playingVoice === voice.id ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-lg">{voice.name}</div>
                  <div className="text-sm text-muted-foreground">{voice.description}</div>
                </div>
                <div className="h-4 w-4 rounded-full border border-primary opacity-0 peer-data-[state=checked]:opacity-100 bg-primary transition-opacity" />
              </Label>
            </div>
          ))}
        </RadioGroup>
      </Card>

      {/* Background Sound Selection */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Volume2 className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Background Ambience</h2>
        </div>

        <RadioGroup value={backgroundSound} onValueChange={setBackgroundSound} className="space-y-3">
          {backgroundSounds.map((sound) => (
            <div key={sound.id} className="relative">
              <RadioGroupItem value={sound.id} id={`sound-${sound.id}`} className="peer sr-only" />
              <Label
                htmlFor={`sound-${sound.id}`}
                className="flex items-center gap-4 rounded-lg border p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
              >
                {sound.id !== 'none' ? (
                  <div
                    className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors z-10"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePlayBackground(sound.id);
                    }}
                  >
                    {playingBackground === sound.id ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </div>
                ) : (
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                    <Volume2 className="w-5 h-5" />
                  </div>
                )}

                <div className="flex-1">
                  <div className="font-semibold text-lg">{sound.name}</div>
                  <div className="text-sm text-muted-foreground">{sound.description}</div>
                </div>
                <div className="h-4 w-4 rounded-full border border-primary opacity-0 peer-data-[state=checked]:opacity-100 bg-primary transition-opacity" />
              </Label>
            </div>
          ))}
        </RadioGroup>
      </Card>

      {/* Advanced Settings */}
      <Card className="p-6 card-hover">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">Advanced</h2>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="greeting">Greeting Message</Label>
            <Input
              id="greeting"
              placeholder="e.g. Thanks for calling Sophie's Salon..."
              value={greetingMessage}
              onChange={(e) => setGreetingMessage(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">The first sentence Sophie will say when answering the phone.</p>
          </div>

          <Collapsible open={isAdvancedSettingsOpen} onOpenChange={setIsAdvancedSettingsOpen} className="space-y-6">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full flex justify-between items-center p-2 h-auto font-medium text-primary hover:text-primary/80 border rounded-md">
                <span>More Advanced Settings</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isAdvancedSettingsOpen ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-6 pt-2">
              <div className="space-y-2">
                <Label htmlFor="restaurantCode">Restaurant Code</Label>
                <Input
                  id="restaurantCode"
                  placeholder="e.g. QC017688369915181FF"
                  value={restaurantCode}
                  onChange={(e) => setRestaurantCode(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">The Libro restaurant code used for reservations.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="googleCalendarId">Google Calendar ID</Label>
                <Input
                  id="googleCalendarId"
                  placeholder="e.g. your-calendar-id@group.calendar.google.com"
                  value={googleCalendarId}
                  onChange={(e) => setGoogleCalendarId(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Optional. If set, Sophie will use this Google calendar instead of your primary calendar.
                </p>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="askLastname">Request customer to provide his lastname</Label>
                  <p className="text-xs text-muted-foreground">When enabled, Sophie will ask callers to spell their last name during booking.</p>
                </div>
                <Switch
                  id="askLastname"
                  checked={askLastname}
                  onCheckedChange={setAskLastname}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxGroupSize">Maximum group size allowed</Label>
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="maxGroupSizeEnabled">Enable maximum group size</Label>
                    <p className="text-xs text-muted-foreground">
                      When disabled, there is no maximum group size.
                    </p>
                  </div>
                  <Switch
                    id="maxGroupSizeEnabled"
                    checked={isMaxGroupSizeEnabled}
                    onCheckedChange={(checked) => {
                      setIsMaxGroupSizeEnabled(checked);
                      if (checked && maxGroupSize <= 0) {
                        setMaxGroupSize(1);
                      }
                      if (!checked) {
                        setMaxGroupSize(-1);
                      }
                    }}
                  />
                </div>
                {isMaxGroupSizeEnabled && (
                  <>
                    <Input
                      id="maxGroupSize"
                      type="number"
                      min={1}
                      step={1}
                      value={maxGroupSize > 0 ? maxGroupSize : 1}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        setMaxGroupSize(Number.isNaN(parsed) ? 1 : Math.max(1, parsed));
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter a positive integer (1 or more).
                    </p>
                  </>
                )}
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="transferTakeout">Transfer to human in case of takeout request</Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, Sophie will transfer takeout-related calls to a real person.
                  </p>
                </div>
                <Switch
                  id="transferTakeout"
                  checked={transferTakeout}
                  onCheckedChange={setTransferTakeout}
                />
              </div>

              {isGodModeEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="instruction">System Instructions</Label>
                  <Textarea
                    id="instruction"
                    placeholder="Provide specific instructions for how Sophie should handle calls..."
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    rows={20}
                    className="resize-none font-mono text-sm"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="voiceRecognition">Voice Recognition Model</Label>
                <Select value={voiceRecognition} onValueChange={setVoiceRecognition}>
                  <SelectTrigger id="voiceRecognition">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HybridShotgun">English / French Auto (recommended)</SelectItem>
                    <SelectItem value="DeepGram">English Only</SelectItem>
                    <SelectItem value="Speechmatics">French Only</SelectItem>
                    <SelectItem value="Whisper">Whisper</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </Card>

      <div className="flex justify-end sticky bottom-6 bg-background/80 backdrop-blur-sm p-4 border rounded-lg shadow-lg">
        <Button size="lg" onClick={saveSettings}>Save Changes</Button>
      </div>

    </div>
  );
}
